import { programById, type LoyaltyProgram } from './loyalty_programs';

/**
 * As marcas de cada programa de HOTEL, para achar o programa pelo NOME do hotel
 * (#6159): a busca do chat (`hotels-api`, `searchHotel`) não expõe a rede, então
 * «Mercure São Paulo Jardins» vira ALL Accor pela marca no nome. Provisório até a
 * busca trazer a rede — aí o casamento passa a ser por código, como o da locadora.
 *
 * Lista CONSERVADORA: só marcas que não confundem. Fora de propósito: Radisson e
 * as bandeiras da Choice (Comfort, Quality, Sleep Inn, Clarion), que no Brasil a
 * Atlantica opera e cujo programa varia por hotel; palavras comuns que também são
 * marca («W», «Element», «Even», «Tru», «Regent», «Tribe»). Hotel sem marca
 * reconhecida não tem programa — melhor nenhuma linha do que a linha errada.
 */
const HOTEL_BRANDS: Readonly<Record<string, readonly string[]>> = {
  'all-accor': [
    'Mercure',
    'Ibis',
    'Novotel',
    'Sofitel',
    'Pullman',
    'Fairmont',
    'Raffles',
    'Swissotel',
    'MGallery',
    'Mövenpick',
    'Adagio',
    'Mantra',
    'Peppers',
    'Grand Mercure',
  ],
  'marriott-bonvoy': [
    'Marriott',
    'Sheraton',
    'Westin',
    'Courtyard',
    'Fairfield',
    'Renaissance',
    'Le Méridien',
    'Ritz-Carlton',
    'St. Regis',
    'Residence Inn',
    'AC Hotel',
    'Four Points',
    'Aloft',
    'Moxy',
    'Protea',
    'Delta Hotels',
    'SpringHill Suites',
    'TownePlace',
    'Gaylord',
    'Autograph Collection',
    'Tribute Portfolio',
  ],
  'hilton-honors': [
    'Hilton',
    'DoubleTree',
    'Hampton Inn',
    'Hampton by Hilton',
    'Embassy Suites',
    'Conrad',
    'Waldorf Astoria',
    'Curio Collection',
    'Canopy by Hilton',
    'Tapestry Collection',
    'Homewood Suites',
    'Home2 Suites',
  ],
  'world-of-hyatt': ['Hyatt', 'Andaz', 'Thompson', 'Alila'],
  'ihg-one-rewards': [
    'InterContinental',
    'Holiday Inn',
    'Crowne Plaza',
    'Hotel Indigo',
    'Kimpton',
    'Staybridge',
    'Candlewood',
    'Six Senses',
    'voco',
  ],
  'wyndham-rewards': [
    'Wyndham',
    'Ramada',
    'Days Inn',
    'Super 8',
    'La Quinta',
    'Tryp',
    'Wingate',
    'Microtel',
    'Baymont',
    'Howard Johnson',
  ],
  'best-western-rewards': ['Best Western', 'SureStay'],
  'melia-rewards': ['Meliá', 'INNSiDE', 'Paradisus'],
  'my-barcelo': ['Barceló'],
  'shangri-la-circle': ['Shangri-La'],
  'fans-of-mo': ['Mandarin Oriental'],
  'unity-by-hard-rock': ['Hard Rock Hotel'],
  'fiesta-rewards': ['Fiesta Americana', 'Fiesta Inn'],
  'star-traveler': ['Eurostars'],
  'leonardo-advantage-club': ['Leonardo Hotel'],
  'scandic-friends': ['Scandic'],
  'jumeirah-one': ['Jumeirah'],
  'sonesta-travel-pass': ['Sonesta'],
  'lets-atlantica': ['Go Inn'],
  'my-wish': ['Wish Hotel', 'Wish Resort'],
  'vila-gale-club': ['Vila Galé'],
  uainn: ['Nacional Inn'],
  'fidelize-laghetto': ['Laghetto'],
  viero: ['Slaviero'],
  'palladium-rewards': ['Palladium'],
  'riu-class': ['Riu'],
  'h10-rewards': ['H10'],
  'catalonia-rewards': ['Catalonia'],
};

/** Caixa, acento e pontuação fora: «Le Méridien» e «LE MERIDIEN» são a mesma
 *  marca. As bordas de espaço deixam casar só a palavra inteira. */
function words(text: string): string {
  const plain = text
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim();
  return ` ${plain} `;
}

const BRAND_INDEX: readonly { brand: string; program: LoyaltyProgram }[] = Object.entries(
  HOTEL_BRANDS,
).flatMap(([id, brands]) => {
  const program = programById(id);
  return program ? brands.map((brand) => ({ brand: words(brand), program })) : [];
});

/** O programa de hotel da marca no nome, ou `undefined` sem marca conhecida. */
export function hotelProgramByName(name: string): LoyaltyProgram | undefined {
  const plain = words(name);
  return BRAND_INDEX.find(({ brand }) => plain.includes(brand))?.program;
}
